import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import type { CallPhase } from './telephony.types';

export interface ActiveCallTarget {
  readonly name: string;
  readonly number: string;
  readonly mode: 'callback' | 'webrtc';
}

/**
 * Singleton tracking the in-progress call visible across the whole app.
 * Updated by CallButton (PBX callback) and WebphoneService (WebRTC).
 * Read by CallOverlay in the shell.
 */
@Injectable({ providedIn: 'root' })
export class ActiveCallService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly phase = signal<CallPhase>('idle');
  readonly target = signal<ActiveCallTarget | null>(null);
  readonly durationSeconds = signal(0);

  readonly isActive = computed(() => this.phase() !== 'idle');
  readonly formattedDuration = computed(() => {
    const s = this.durationSeconds();
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  });

  private timer: ReturnType<typeof setInterval> | null = null;
  private endTimeout: ReturnType<typeof setTimeout> | null = null;

  start(target: ActiveCallTarget): void {
    this.clearTimers();
    this.target.set(target);
    this.phase.set('connecting');
    this.durationSeconds.set(0);
  }

  setPhase(phase: CallPhase): void {
    this.phase.set(phase);

    if (phase === 'in-call') {
      this.startTimer();
    }

    if (phase === 'ended' || phase === 'error') {
      this.stopTimer();
      // Auto-clear after 4 s so the overlay has time to show the final state.
      this.endTimeout = this.isBrowser
        ? setTimeout(() => this.reset(), 4000)
        : null;
    }
  }

  /** Hook the WebRTC hang-up, wired by WebphoneService to avoid a circular DI. */
  webrtcHangUp: (() => void) | null = null;

  /** Hang up the current call (WebRTC widget) and mark ended. */
  hangUp(): void {
    if (this.target()?.mode === 'webrtc') {
      this.webrtcHangUp?.();
    }
    this.setPhase('ended');
  }

  /** Dismiss the overlay for PBX callback calls (audio is on the physical phone). */
  dismiss(): void {
    this.reset();
  }

  private startTimer(): void {
    if (this.timer || !this.isBrowser) return;
    this.timer = setInterval(() => this.durationSeconds.update((v) => v + 1), 1000);
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private clearTimers(): void {
    this.stopTimer();
    if (this.endTimeout) {
      clearTimeout(this.endTimeout);
      this.endTimeout = null;
    }
  }

  private reset(): void {
    this.clearTimers();
    this.phase.set('idle');
    this.target.set(null);
    this.durationSeconds.set(0);
  }
}
